import type Article from '@gik/blog/models/Article';
import type { Layout } from '@gik/core/types/UI';
import { useBemCN } from '@gik/core/utils/bemBlock';
import type { IArticleTileProps } from '@gik/ui/gik/ArticleTile';
import { ArticleTile } from '@gik/ui/gik/ArticleTile';
import { ParagraphBone } from '@gik/ui/SkeletonLoader';
import React from 'react';

export interface IArticlesGridProps extends Omit<IArticleTileProps, 'article' | 'layout'> {
  title?: string;
  articles: Article[];
  filter?: (articles: Article[]) => Article[];
  centeredTitle?: boolean;
  className?: string;
  layout?: Layout;
  tileLayout?: Layout;
}

const blockName = 'articles-grid';

export function ArticlesGrid({
  title,
  articles,
  filter = x => x,
  centeredTitle,
  className,
  layout = 'auto',
  tileLayout = 'auto',
  ...articleTileProps
}: IArticlesGridProps) {
  const bem = useBemCN(blockName);

  const filteredArticles = React.useMemo(() => filter(articles), [articles, filter]);
  if (!filteredArticles || filteredArticles.length === 0) return null;

  const titleEl = (
    <>
      {title && (
        <h1 {...bem('title')}>
          {articleTileProps.skeleton ? <ParagraphBone words={title?.split(' ').length || 2} /> : title}
        </h1>
      )}
    </>
  );

  return (
    <section
      {...bem(
        null,
        [{ [`layout-${layout}`]: true }, { 'centered-title': centeredTitle }, { skeleton: articleTileProps.skeleton }],
        className
      )}
    >
      {titleEl}

      <div {...bem('articles')}>
        {filteredArticles?.map((article, key) => (
          <ArticleTile key={key} article={article} {...articleTileProps} layout={tileLayout} />
        ))}
      </div>
    </section>
  );
}
